import { z } from 'zod';
import { typeResourcesSchema } from './catalog.js';

export { decimalString, positiveDecimal, rate } from './primitives.js';
import { decimalString, positiveDecimal, rate } from './primitives.js';


/** Paramètres d'un type, d'une variante ou d'une instance : { a: "1.20", b: "1.20" } ; null = effacer. */
export const paramValuesSchema = z.record(z.string().regex(/^[\p{L}_][\p{L}\p{N}_]*$/u), positiveDecimal.nullable());
export type ParamValuesInput = z.infer<typeof paramValuesSchema>;

const optionalText = (max = 300) => z.string().trim().max(max).nullable().optional();

export const PROJECT_STATUSES = ['DRAFT', 'IN_PROGRESS', 'DONE', 'ARCHIVED'] as const;
export const PRICING_MODES = ['CONTRACTOR', 'DIRECT_LABOR', 'BOTH'] as const;
export const ZONE_KINDS = ['BUILDING', 'ANNEX', 'FENCE', 'TECHNICAL', 'TANK', 'POOL', 'PIT', 'PARKING', 'VRD', 'OTHER'] as const;
export const LEVEL_KINDS = ['FOUNDATION', 'BASEMENT', 'GROUND', 'UPPER', 'ROOF', 'OTHER'] as const;

export const projectSettingsSchema = z.object({
  vatEnabled: z.boolean().optional(),
  vatRate: rate.optional(),
  defaultLossRate: rate.optional(),
  contingencyRate: rate.optional(),
  contingencyBase: z.enum(['DIRECT_COST', 'MATERIALS']).optional(),
  siteOverheadRate: rate.optional(),
  generalOverheadRate: rate.optional(),
  marginRate: rate.optional(),
  rebarCommercialLength: positiveDecimal.optional(),
});
export type ProjectSettingsInput = z.infer<typeof projectSettingsSchema>;

/** Gabarit de niveaux à la création : Fondation, RDC, R+1…R+n, Toiture, avec leurs hauteurs. */
export const levelTemplateSchema = z.object({
  zoneName: z.string().trim().min(1).max(120).default('Bâtiment principal'),
  foundation: z.boolean().default(true),
  groundHeight: positiveDecimal.default('3.20'),
  upperLevels: z.number().int().min(0).max(60).default(0),
  upperHeight: positiveDecimal.default('3.00'),
  roof: z.boolean().default(true),
});

const projectFields = {
  name: z.string().trim().min(2).max(200),
  reference: z.string().trim().min(1).max(60).optional(),
  clientName: optionalText(200),
  ownerName: optionalText(200),
  contractorName: optionalText(200),
  engineerName: optionalText(200),
  architectName: optionalText(200),
  location: optionalText(300),
  projectDate: z.iso.date().nullable().optional(),
  buildingType: optionalText(120),
  grossArea: positiveDecimal.nullable().optional(),
  description: optionalText(5000),
  observations: optionalText(5000),
  currency: z.enum(['XAF', 'XOF', 'EUR', 'USD']).optional(),
  status: z.enum(PROJECT_STATUSES).optional(),
  pricingMode: z.enum(PRICING_MODES).optional(),
};

export const projectCreateSchema = z.object({
  ...projectFields,
  settings: projectSettingsSchema.optional(),
  template: levelTemplateSchema.optional(),
});
export type ProjectCreateInput = z.input<typeof projectCreateSchema>;

export const projectUpdateSchema = z
  .object({ ...projectFields, settings: projectSettingsSchema.optional() })
  .partial()
  .extend({ version: z.number().int().positive() });
export type ProjectUpdateInput = z.input<typeof projectUpdateSchema>;

export const zoneSchema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(120),
  kind: z.enum(ZONE_KINDS).default('BUILDING'),
  sortOrder: z.number().int().optional(),
});

export const levelSchema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(120),
  kind: z.enum(LEVEL_KINDS).default('UPPER'),
  height: positiveDecimal.nullable().optional(),
  elevation: decimalString.nullable().optional(),
  area: positiveDecimal.nullable().optional(),
  sortOrder: z.number().int().optional(),
});

export const elementTypeSchema = z.object({
  familyId: z.uuid(),
  zoneId: z.uuid().nullable().optional(),
  code: z.string().trim().min(1).max(40),
  name: optionalText(160),
  description: optionalText(2000),
  params: paramValuesSchema.default({}),
  color: optionalText(20),
  resources: z.lazy(() => typeResourcesSchema).optional(),
});
export const elementTypeUpdateSchema = elementTypeSchema.omit({ familyId: true }).partial().extend({ version: z.number().int().positive() });

export const variantSchema = z.object({ params: paramValuesSchema, notes: optionalText(2000) });

export const instanceSchema = z.object({
  typeId: z.uuid(),
  levelId: z.uuid(),
  mode: z.enum(['AGGREGATE', 'DETAILED']).default('AGGREGATE'),
  quantity: positiveDecimal.default('1'),
  label: optionalText(80),
  params: paramValuesSchema.default({}),
});
export const instanceUpdateSchema = z
  .object({ quantity: positiveDecimal, label: optionalText(80), params: paramValuesSchema, quantitySource: z.enum(['AUTO', 'MANUAL']) })
  .partial()
  .extend({ version: z.number().int().positive() });

export const segmentsSchema = z.array(z.object({ label: z.string().trim().min(1).max(20), length: positiveDecimal, params: paramValuesSchema.default({}) })).max(200);
export const openingsSchema = z.array(z.object({ code: optionalText(40), width: positiveDecimal, height: positiveDecimal, count: positiveDecimal.default('1') })).max(500);

/** « Dupliquer vers les niveaux supérieurs ». */
export const copyLevelSchema = z.object({
  targetLevelIds: z.array(z.uuid()).min(1),
  typeIds: z.array(z.uuid()).optional(),
  /** Copie aussi les variantes de section du niveau source. */
  includeVariants: z.boolean().default(true),
});

const familyParameterSchema = z.object({
  key: z.string().regex(/^[\p{L}_][\p{L}\p{N}_]*$/u),
  label: z.string().trim().min(1).max(120),
  unit: z.string().max(30),
  default: decimalString.optional(),
  min: decimalString.optional(),
  max: decimalString.optional(),
  fromLevel: z.literal('height').optional(),
  section: z.boolean().optional(),
  integer: z.boolean().optional(),
});
const familyOutputSchema = z.object({
  key: z.string().regex(/^[\p{L}_][\p{L}\p{N}_]*$/u),
  label: z.string().trim().min(1).max(120),
  unit: z.string().min(1).max(30),
  formula: z.string().trim().min(1).max(2000),
  primary: z.boolean().optional(),
  kind: z.enum(['CONCRETE', 'FORMWORK', 'BLINDING', 'EXCAVATION', 'BACKFILL', 'MASONRY', 'MASONRY_VOLUME', 'AREA', 'LENGTH', 'VOLUME', 'COUNT', 'OTHER']).optional(),
});
export const familyDefinitionSchema = z.object({
  code: z.string().trim().regex(/^[A-Z0-9_-]{2,40}$/, { error: 'Code en majuscules (A-Z, 0-9, _ -)' }),
  name: z.string().trim().min(2).max(120),
  group: z.enum(['FOUNDATION', 'VERTICAL', 'HORIZONTAL', 'MASONRY', 'FINISH', 'ROOF', 'EARTHWORK', 'OTHER']),
  description: z.string().max(2000).optional(),
  parameters: z.array(familyParameterSchema).min(1).max(40),
  outputs: z.array(familyOutputSchema).min(1).max(20),
  segmentable: z.boolean().optional(),
  segmentLengthKey: z.string().optional(),
  openings: z.boolean().optional(),
  buried: z.boolean().optional(),
  chapter: z.string().regex(/^[A-Z0-9_-]{2,20}$/).optional(),
  lineByType: z.boolean().optional(),
  defaults: z
    .object({ concrete: z.string().max(30).optional(), blinding: z.string().max(30).optional(), masonry: z.string().max(30).optional(), mortar: z.string().max(30).optional(), kits: z.array(z.string().max(30)).max(10).optional() })
    .optional(),
});
export type FamilyDefinitionInput = z.infer<typeof familyDefinitionSchema>;
